import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import { logClientError } from "@/lib/errorLogger";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Critical app error:", {
      error,
      errorInfo,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString()
    });

    void logClientError(error, "ErrorBoundary", { componentStack: errorInfo.componentStack });


    this.setState({
      error,
      errorInfo
    });
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-cyan-light to-white p-4">
          <Card className="max-w-md w-full p-6 sm:p-8 text-center space-y-5 shadow-xl border-[3px] border-border rounded-3xl overflow-hidden">
            <div className="flex justify-center">
              <div className="rounded-full bg-secondary/20 p-5">
                <AlertCircle className="w-12 h-12 text-secondary-foreground" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-2xl sm:text-3xl font-heading font-black text-foreground">
                Oeps, even opnieuw! 🔄
              </h1>
              <p className="text-muted-foreground text-base sm:text-lg">
                Er ging iets mis, maar no worries – probeer het nog een keer!
              </p>
            </div>

            {this.state.error && process.env.NODE_ENV === 'development' && (
              <div className="bg-muted/50 p-3 rounded-xl text-left">
                <p className="text-xs font-semibold mb-1 text-foreground/70">
                  Debug info:
                </p>
                <p className="text-xs font-mono text-destructive break-all">
                  {this.state.error.message}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
              <Button
                onClick={() => window.location.reload()}
                variant="default"
                size="default"
                className="gap-2 flex-1 bg-secondary text-secondary-foreground hover:bg-secondary/90"
              >
                <RefreshCw className="w-4 h-4" />
                Opnieuw laden
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                variant="outline"
                size="default"
                className="gap-2 flex-1"
              >
                <Home className="w-4 h-4" />
                Naar home
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              Blijft het hangen? {" "}
              <a href="/support" className="text-primary hover:underline font-medium">
                We helpen je! 💬
              </a>
            </p>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
